/**
 * The people a report can be shared with: fetching them, and turning the Users layer's rows
 * into an ordered, de-duplicated list.
 */

import UserService from '../../../../services/UserService.jsx';
import { getAppConfig, getAttributeServiceUrl } from '../../../../utils/ReduxHelper.jsx';
import FieldNames from '../../../../defaults/FieldNames.json';

/** A row from the Users layer, of which the share picker reads five fields. */
export interface ShareCandidate {
    [key: string]: unknown;
}

const USER = FieldNames.userFields;

export const userName = (user: ShareCandidate): string => String(user[USER.nifcUsername] ?? '');

const firstLast = (user: ShareCandidate): [string, string] => [
    String(user[USER.firstName] ?? '').trim(),
    String(user[USER.lastName] ?? '').trim(),
];

/** "Thomas, Ashley" — surname first, so a scanned column lines up with the letter rails. */
export function directoryName(user: ShareCandidate): string {
    const [first, last] = firstLast(user);
    if (last && first) return `${last}, ${first}`;
    return last || first || userName(user);
}

/** "Ashley Thomas (athomas)", falling back to the username when the row carries no name. */
export function describeUser(user: ShareCandidate): string {
    const name = firstLast(user).filter(Boolean).join(' ');
    return name ? `${name} (${userName(user)})` : userName(user);
}

const sortKey = (user: ShareCandidate): string => directoryName(user).toLowerCase();

/** The letter rail. Anything not starting with a letter files under #. */
export function groupLetter(user: ShareCandidate): string {
    const first = sortKey(user).charAt(0).toUpperCase();
    return first >= 'A' && first <= 'Z' ? first : '#';
}

/** ArcGIS date fields arrive as epoch milliseconds; a row that has never signed in has none. */
const lastLogin = (user: ShareCandidate): number =>
    typeof user[USER.lastLogin] === 'number' ? (user[USER.lastLogin] as number) : 0;

/**
 * One row per key, preferring whoever signed in most recently — that is the account the
 * person actually uses. A row with no key stands only for itself.
 */
function collapse(
    rows: ShareCandidate[],
    keyOf: (user: ShareCandidate) => string
): ShareCandidate[] {
    const best = new Map<string, ShareCandidate>();
    rows.forEach((row, index) => {
        const key = keyOf(row) || `row:${index}`;
        const held = best.get(key);
        if (!held || lastLogin(row) > lastLogin(held)) best.set(key, row);
    });
    return [...best.values()];
}

/**
 * Sharing with yourself, with someone who already has it, or with a deactivated account all
 * do nothing, so none is offered.
 *
 * What reaches the share table is the username, so rows are collapsed on that first: two
 * rows carrying one username are one recipient, and offering both would write the same row
 * twice under the same React key.
 *
 * Collapsing on email then folds the several accounts one person can hold into the one they
 * last signed in with. Two usernames are two grantable identities, so this is a choice
 * rather than a merge — sharing reaches the account kept, not the other one.
 */
export function buildShareOptions(
    candidates: ShareCandidate[],
    excluded: Iterable<string>
): ShareCandidate[] {
    const taken = new Set(excluded);
    const active = candidates.filter((user) => {
        const username = userName(user);
        if (!username || taken.has(username)) return false;
        return user[USER.isActive] === FieldNames.yesNo.yes;
    });

    const oneRowPerUsername = collapse(active, (user) => userName(user).toLowerCase());
    const oneRowPerPerson = collapse(oneRowPerUsername, (user) =>
        String(user[USER.email] ?? '')
            .trim()
            .toLowerCase()
    );
    return oneRowPerPerson.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
}

export async function loadShareCandidates(): Promise<ShareCandidate[]> {
    const users = await UserService.getAllUsers(getAttributeServiceUrl(), getAppConfig());
    return users ?? [];
}
